import { TutorEducation } from "src/interfaces/TutorProfile";

type EducationInput = unknown;

const emptyEducation = (): TutorEducation => ({
  degree: "",
  field: "",
  institution: "",
  country: "",
  year_of_completion: 0,
  is_custom_degree: false,
  level: ""
});

const toEducationRecord = (value: unknown): TutorEducation | null => {
  if (typeof value === "string") {
    const degree = value.trim();
    return degree ? { ...emptyEducation(), degree } : null;
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const entry = value as Record<string, unknown>;
  const numberOrUndefined = (input: unknown) => {
    const number = Number(input);
    return Number.isFinite(number) ? number : undefined;
  };

  return {
    ...emptyEducation(),
    degree: typeof entry.degree === "string" ? entry.degree : "",
    field: typeof entry.field === "string" ? entry.field : "",
    institution: typeof entry.institution === "string" ? entry.institution : "",
    country: typeof entry.country === "string" ? entry.country : "",
    level: typeof entry.level === "string" ? entry.level : "",
    year_of_completion: numberOrUndefined(entry.year_of_completion ?? entry.year) ?? 0,
    gpa: numberOrUndefined(entry.gpa),
    gpa_total: numberOrUndefined(entry.gpa_total),
    is_custom_degree: Boolean(entry.is_custom_degree)
  };
};

/** Converts legacy tutor education values into the profile's array format. */
export const normalizeTutorEducation = (value: EducationInput): TutorEducation[] => {
  if (Array.isArray(value)) {
    return value
      .map(toEducationRecord)
      .filter((entry): entry is TutorEducation => entry !== null);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed !== value) return normalizeTutorEducation(parsed);
    } catch {
      // Older admin edits stored education as plain text.
    }

    return [{ ...emptyEducation(), degree: trimmed }];
  }

  const record = toEducationRecord(value);
  return record ? [record] : [];
};
